<script setup lang="ts">
import {computed, provide, ref, watch, watchEffect} from 'vue';
import {useI18n} from 'vue-i18n';
import {useApi} from '@/ts/composables/stateful/use-api';
import {persistentStorage} from '@/ts/instances/persistant-storage';
import {useLoadingDataRefNull} from '@/ts/composables/pure/use-loading-data-ref';
import {useLoadingSpinner} from '@/ts/composables/pure/use-loading-notification';
import {useLang} from '@/ts/composables/stateful/use-lang';
import {useSignIn} from '@/ts/composables/stateful/use-auth';
import {useCompleteSignUp} from '@/ts/composables/stateful/use-complete-sign-up';
import SignUpEmail from '@/vue/organisms/sign-up/sign-up-email.vue';
import SignUpPersonalize from '@/vue/organisms/sign-up/sign-up-personalize.vue';
import SignUpStepper from '@/vue/atoms/stepper/sign-up-stepper.vue';
import BalmStep from '@/vue/atoms/stepper/balm-step.vue';
import SignUpVerificationSent from '@/vue/organisms/sign-up/sign-up-verification-sent.vue';
import SignUpConsentForm from '@/vue/organisms/sign-up/sign-up-consent-form.vue';
import SignUpTrial from '@/vue/organisms/sign-up/sign-up-trial.vue';
import SignUpCaptcha from '@/vue/organisms/sign-up/sign-up-captcha.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import WarningNote from '@/vue/atoms/warning-note.vue';
import {consentSelectionKey, useConsentSelection} from '@/ts/composables/pure/use-consent-selection';
import SignUpLegalDocsModal from '@/vue/molecules/sign-up-legal-docs-modal.vue';
import LoadingError from '@/vue/templates/loading-error.vue';
import PublicAuthTemplate from '@/vue/templates/public-auth-template.vue';
import {LegalModel} from '@/ts/types/component/public-sign-up-page';
import type {NavigationItem} from '@/ts/types/component/navigation';
import type {SignUpCaptchaApi} from '@/ts/types/component/sign-up-captcha';
import {useRoute} from 'vue-router';
import {RegistrationStepId} from '@/ts/types/component/sign-up-type';
import {BlacklistedFeature, EducationLevelDTO, type ValidateVoucherResponse} from '@/ts/types/dto/sign-in-dto';
import type {GetLegalDocsResponse} from '@/ts/types/dto/tos-dto';
import {PRIVATE_CAPTCHA_SITE_KEY, SIGN_UP_FORM_DEFAULT_VALUES, ZITADEL} from '@/ts/utils/consts';
import {GenderDTO} from '@/ts/types/dto/gender-dto';
import {AppError} from '@/ts/utils/errors';
import {AppErrorCode, ErrorCodesDto} from '@/ts/types/dto/error-codes-dto';

const api = useApi();
const route = useRoute();
const {t} = useI18n();
const {lang} = useLang();
const signIn = useSignIn();

const completeSignUp = useCompleteSignUp();
const isCompleteSignUp = completeSignUp.isActive;

// A token on this page is not enough to say the visitor is mid-registration: someone already
// signed in can land here from a campaign link, a bookmark or the back button, and hiding the form
// from them would leave a screen whose only way out signs them out of a healthy session. So ask —
// and ask inside the page's existing load gate, which keeps it off the first paint either way.
const isGoogleSignUpPending = ref(false);

const [genericLegalDocs, loading, error] = useLoadingDataRefNull(async() => {
  const [legalDocs, registration] = await Promise.all([
    api.public.getLegalDocs(),
    // Gated like the other two probe sites: where the environment has no Google provider there are
    // no half-finished Google registrations to detect, the answer would be discarded anyway, and
    // the call is a 404 — one Sentry issue per visit — until the endpoint is deployed. The
    // shortened flow skips it too: continue-auth just asked, and the answer it got is what sent
    // the user here, so the email step this would feed never renders.
    ZITADEL.google_idp_id && persistentStorage.auth && !isCompleteSignUp.value
      ? api.signUp.getRegistrationStatus().catch(() => ({registered: true}))
      : Promise.resolve({registered: true}),
  ]);
  isGoogleSignUpPending.value = !registration.registered;
  return legalDocs;
});

// The Google attempt that ended before an account existed says why in the URL, so the first screen
// can explain it after the round trip through Zitadel, which echoes the reason back as `state`
// (see use-complete-sign-up). Read as an allow-list: anything else in the parameter is not ours
// to render.
const signUpExitMessage = computed((): string => {
  switch (route.query?.state) {
    case ErrorCodesDto.EMAIL_ALREADY_REGISTERED:
      return t('google_sign_up_email_taken');
    case ErrorCodesDto.AUTH_SERVER_EMAIL_UNVERIFIED:
      return t('auth_server_email_unverified');
    case AppErrorCode.SESSION_EXPIRED:
      // The forced logout lands here too, and its toast does not survive the redirect.
      return t('session_expired');
    default:
      return '';
  }
});

// A redeemed voucher's organization may enforce its own terms of service. Those
// are loaded when leaving the trial step (before consent) so the user reviews
// and accepts the org-specific documents — and they are submitted to match what
// the BE resolves from the voucher code (otherwise it rejects with
// `mismatch_tos_version`). Falls back to the generic docs for the free-trial path.
const voucherLegalDocs = ref<GetLegalDocsResponse | null>(null);
const currentLegalDocs = computed((): GetLegalDocsResponse | null => voucherLegalDocs.value ?? genericLegalDocs.value);

// Wizard step index (verification is shown as a modal, not a carousel step).
const step = ref(0);

// Lifted form state, collected across steps and submitted at the end (Trial step).
// Optionally seeded from SIGN_UP_FORM_DEFAULT_VALUES for local-dev convenience.
const email = ref<string>(SIGN_UP_FORM_DEFAULT_VALUES?.email ?? '');
const name = ref<string>(SIGN_UP_FORM_DEFAULT_VALUES?.name ?? '');
const birthDate = ref<Date | null>(
  SIGN_UP_FORM_DEFAULT_VALUES?.birthDate ? new Date(`${SIGN_UP_FORM_DEFAULT_VALUES.birthDate}T00:00:00`) : null,
);
const voucherCode = ref<string>('');
const marketingCodeParam = ref<string>('');

// The validated voucher's organization name drives partner branding on the auth
// template (see use-partner-branding, which injects this ref). Cleared whenever
// the voucher sub-screen collapses so the branding tracks the active voucher.
const voucherOrganizationName = ref<string | null>(null);
provide('voucherOrganizationName', voucherOrganizationName);

const captchaIsLoading = ref(false);
// The captcha CDN script failed to load: rendered here (not inside the captcha
// component) because carousel slide content doesn't re-render on child state.
const captchaScriptFailed = ref(false);
const voucherRevealed = ref(false);
const showVerification = ref(false);
const emailError = ref<string>('');

// Some voucher organizations blacklist the captcha (e.g. partner study devices
// where the challenge would block enrolment). When a validated voucher reports
// CAPTCHA in `blacklistedFeatures`, the captcha step is dropped from the wizard
// and the registration submits without a token. Reset whenever the voucher is
// collapsed/cleared so the default (captcha-active) flow is restored.
const captchaBlacklistedByVoucher = ref(false);

// Trial (where the voucher is entered) precedes consent so a voucher's
// organization-specific terms can be loaded and shown before the user accepts.
const steps = computed((): RegistrationStepId[] => [
  ...(isCompleteSignUp.value ? [] : [RegistrationStepId.EMAIL]),
  RegistrationStepId.USER_DATA,
  RegistrationStepId.TRIAL,
  RegistrationStepId.CONSENT,
  ...(PRIVATE_CAPTCHA_SITE_KEY && !captchaBlacklistedByVoucher.value && !isCompleteSignUp.value
    ? [RegistrationStepId.CAPTCHA]
    : []),
]);

const getStepIndexOf = (stepId: RegistrationStepId): number => steps.value.indexOf(stepId);
const consentStepIndex = computed((): number => getStepIndexOf(RegistrationStepId.CONSENT));

// Consent selection state shared between the consent form (checkboxes) and the
// stepper footer (Essentials Only / Accept All / Accept and Continue).
const consent = useConsentSelection();
provide(consentSelectionKey, consent);
const {
  canContinue, acceptAll, essentialsOnly,
  isEssentialsOnlyActive, isAcceptAllActive,
  optionalExpanded,
  productImprovement, research, analyticsAnonymized,
} = consent;

const consentFormRef = ref<typeof SignUpConsentForm>(null!);

// "Accept All" selects the optional consents too, so smooth-scroll to the end
// to reveal them — letting the user see what was accepted on their behalf.
function onAcceptAll(): void {
  const wasExpanded = optionalExpanded.value;
  acceptAll();
  (consentFormRef.value as unknown as {revealOptionalResult(wasExpanded: boolean): void} | null)
    ?.revealOptionalResult(wasExpanded);
}

const legalModel = ref<LegalModel | null>(null);
const personalizeRef = ref<typeof SignUpPersonalize>(null!);
const trialRef = ref<typeof SignUpTrial>(null!);
const captchaRef = ref<SignUpCaptchaApi | null>(null);

const footerItems = ref<NavigationItem[]>([
  {id: LegalModel.IMPRINT, labelKey: 'imprint'},
  {id: LegalModel.TERMS_OF_SERVICE, labelKey: 'terms_of_use'},
  {id: LegalModel.PRIVACY_POLICY, labelKey: 'privacy_policy'},
]);

// Tracks whether the consent step has already been completed (advanced past)
// in this session, so a later forward re-entry — e.g. after a duplicate-email
// bounce sends the user back to the email step — keeps the selection instead of
// wiping it, consistent with the other retained fields (email/name/dob).
const consentCompleted = ref(false);

// Same rationale for the trial step: once passed, a forward re-entry (e.g. the
// duplicate-email bounce) must keep the revealed voucher and its code rather than
// collapsing back to the free-trial view and silently dropping the voucher.
const trialCompleted = ref(false);

// Keep the consent step coherent across navigation:
// - First forward entry starts fresh, so it behaves like a first visit.
// - Any later entry (Back from a later step, or a forward re-entry after the
//   step was already completed) keeps the selection (the sign-up payload depends
//   on it) and expands the optional group if any optional consent is ticked.
// - The trial step's "I have a voucher" reveal is mirrored on the parent so it
//   survives the carousel remount. Collapse it back to the free-trial view only
//   on a fresh forward entry (from the personalize step); arriving Back from the
//   consent step keeps the redeem-voucher sub-screen (and its code) already filled in.
watch(step, (newStep, oldStep) => {
  const trialStepIndex = getStepIndexOf(RegistrationStepId.TRIAL);
  if (oldStep === consentStepIndex.value && newStep > oldStep) {
    consentCompleted.value = true;
  }
  if (oldStep === trialStepIndex && newStep > oldStep) {
    trialCompleted.value = true;
  }
  // Collapse the voucher sub-screen only on a genuinely fresh forward entry; once
  // the trial step has been completed, keep the revealed voucher (and its code).
  if (newStep === trialStepIndex && oldStep < newStep && !trialCompleted.value) {
    voucherRevealed.value = false;
  }
  if (newStep !== consentStepIndex.value) {
    return;
  }
  if (oldStep < newStep && !consentCompleted.value) {
    consent.reset();
  } else {
    consent.revealOptionalIfSelected();
  }
});

// A consent given for one set of documents must not carry over to another:
// reset the selection (and the completed flag) whenever the effective
// terms/privacy documents change — e.g. redeeming an org voucher after having
// accepted the generic docs on the free-trial path, or collapsing the voucher
// back to the free trial. Re-entries with unchanged documents (like the
// duplicate-email bounce) still retain the selection.
watch(
  () => [currentLegalDocs.value?.termsAndConditions.id, currentLegalDocs.value?.privacyPolicy.id] as const,
  ([termsId, privacyId], [previousTermsId, previousPrivacyId]) => {
    const isInitialLoad = !previousTermsId && !previousPrivacyId;
    if (isInitialLoad || (termsId === previousTermsId && privacyId === previousPrivacyId)) {
      return;
    }
    consent.reset();
    consentCompleted.value = false;
  },
);

const nextLabelKey = computed((): string => {
  if (step.value === getStepIndexOf(RegistrationStepId.TRIAL)) {
    return voucherRevealed.value ? 'continue_with_voucher' : 'continue_with_trial';
  }
  return 'wizard_continue';
});

// Gender and education are no longer collected during sign-up (moved to the
// post-sign-up onboarding questionnaire). The BE still requires both fields, so
// we submit `prefer_not_to_say` until the BE makes them optional (IPD-2815).
const isDuplicatedEmail = ref(false);
const [doSignUp] = useLoadingSpinner(
  async(captchaToken: string | null) => {
    isDuplicatedEmail.value = false;
    const marketingCode = marketingCodeParam.value?.trim() || null;
    const trimmedVoucherCode = voucherRevealed.value && voucherCode.value.trim() ? voucherCode.value.trim() : null;
    // `currentLegalDocs` already reflects the voucher organization's terms when
    // a voucher was redeemed (loaded before the consent step the user accepted),
    // so the submitted ids match what the BE resolves from the voucher code —
    // otherwise it rejects with `mismatch_tos_version`.
    try {
      // Built inside the try: the non-null assertions below can throw, and that has to reach the
      // captcha reset in the catch like any other failed attempt.
      const payload = {
        birthYear: birthDate.value!.getFullYear(),
        gender: GenderDTO.PREFER_NOT_TO_SAY,
        name: name.value.trim(),
        termsAndConditionsId: currentLegalDocs.value!.termsAndConditions.id,
        privacyPolicyId: currentLegalDocs.value!.privacyPolicy.id,
        educationLevel: EducationLevelDTO.PREFER_NOT_TO_SAY,
        ...(marketingCode ? {marketingCode} : {}),
        languageCode: lang.value,
        analyticsOptIn: productImprovement.value,
        researchOptIn: research.value,
        analyticsAnonymizedOptIn: analyticsAnonymized.value,
        ...(trimmedVoucherCode ? {voucherCode: trimmedVoucherCode} : {}),
      };
      if (isCompleteSignUp.value) {
        await completeSignUp.submit(payload);
        return;
      }
      await api.public.signUp({
        ...payload,
        email: email.value.trim(),
        captchaToken,
      });
    } catch (err) {
      // Private Captcha tokens are single-use, so any failed attempt (including
      // the duplicate-email bounce back to the email step) must force a fresh
      // challenge before the user can retry — otherwise the next submit reuses a
      // consumed token the BE will reject again.
      await captchaRef.value?.resetAndRemount();
      if (isCompleteSignUp.value && await completeSignUp.absorbError(err)) {
        return;
      }
      // The address the user typed is taken — an answer only the long flow can get, and only it
      // has the email step that reports it. Swallowing it in the shortened flow would report
      // success and send the user home with no account.
      if (!isCompleteSignUp.value && err instanceof AppError && err.statusCode === ErrorCodesDto.DUPLICATED_EMAIL) {
        isDuplicatedEmail.value = true;
        return;
      }
      throw err;
    }
  },
  () => {
    if (isCompleteSignUp.value) {
      completeSignUp.finish();
    } else if (isDuplicatedEmail.value) {
      step.value = getStepIndexOf(RegistrationStepId.EMAIL);
      emailError.value = t('email_already_registered');
    } else {
      // Kept until the first login so the verify-email page can prefill
      // Zitadel's login via login_hint (the emailed link carries no email).
      persistentStorage.signUpEmail = email.value.trim();
      showVerification.value = true;
    }
  },
);

// Loads the voucher organization's legal docs (or clears them for the free-trial
// path) and only advances to the consent step on success, so the user always
// reviews the terms that will actually be submitted.
const [loadVoucherLegalDocsAndAdvance] = useLoadingSpinner(
  async() => {
    voucherLegalDocs.value = voucherRevealed.value && voucherCode.value.trim()
      ? await api.public.getLegalDocs(voucherCode.value.trim())
      : null;
  },
  () => {
    step.value++;
  },
);

// Back from the trial step with the voucher revealed collapses it to the
// free-trial view (the voucher is a sub-screen of the step), instead of leaving
// to the previous step.
function goBack(): void {
  if (steps.value[step.value] === RegistrationStepId.TRIAL && voucherRevealed.value) {
    voucherRevealed.value = false;
    return;
  }
  step.value--;
}

async function goNext(): Promise<void> {
  const current = steps.value[step.value];
  // The email step self-submits via its own button (@finish), so it is never
  // driven through the stepper footer here.
  if (current === RegistrationStepId.USER_DATA) {
    (personalizeRef.value as unknown as {submitForm(): void} | null)?.submitForm();
  } else if (current === RegistrationStepId.TRIAL) {
    const valid = await (trialRef.value as unknown as {validateForm(): Promise<boolean>}).validateForm();
    if (!valid) {
      return;
    }
    // A redeemed voucher may carry org-specific terms; load them (and only then
    // advance to the consent step) so the user reviews and accepts the right docs.
    void loadVoucherLegalDocsAndAdvance();
  } else if (current === RegistrationStepId.CONSENT) {
    // Consent is the last data step: go to captcha if present, otherwise register.
    if (steps.value.includes(RegistrationStepId.CAPTCHA)) {
      step.value++;
    } else {
      // Without a captcha step there is no forward navigation to flag the step
      // as completed, so mark it here — a duplicate-email bounce then retains
      // the selection (for unchanged documents), same as the captcha flow.
      consentCompleted.value = true;
      void doSignUp(null);
    }
  } else if (current === RegistrationStepId.CAPTCHA) {
    void doSignUp(captchaRef.value?.captchaToken ?? null);
  } else {
    step.value++;
  }
}

// Once the user dismisses the verification dialog, send them to login so they
// can sign in after confirming their email. Deliberately NO login_hint here:
// hinting the just-registered (still unverified) email makes Zitadel skip its
// login form and land directly on its hosted verify screen with an empty code —
// the plain login form is the expected landing (the verify-email page's own
// "Log in" button, used after verification, does prefill the email).
const [onVerificationClosed] = useLoadingSpinner(async() => signIn());

function onClickFooterItem(itemId: string): void {
  legalModel.value = itemId as LegalModel;
}

const hasCaptchaBlacklisted = (response: ValidateVoucherResponse): boolean =>
  response.blacklistedFeatures?.includes(BlacklistedFeature.CAPTCHA) ?? false;

const onVoucherValidated = (validatedVoucherCode: string, response: ValidateVoucherResponse): void => {
  voucherCode.value = validatedVoucherCode;
  // validateVoucher only emits on a valid response, so this drives whether the
  // captcha step survives in `steps` before goNext reads it (the emit is
  // synchronous, so the computed re-evaluates in time).
  captchaBlacklistedByVoucher.value = response.valid && hasCaptchaBlacklisted(response);
  voucherOrganizationName.value = response.valid ? (response.organization?.name ?? null) : null;
};

// Collapsing or clearing the voucher (Back from trial, or a fresh forward entry
// that resets the reveal) restores the default captcha-active flow and drops any
// org-specific terms so the consent step falls back to the generic docs.
watch(voucherRevealed, (revealed) => {
  if (!revealed) {
    captchaBlacklistedByVoucher.value = false;
    voucherLegalDocs.value = null;
    voucherOrganizationName.value = null;
  }
});

watchEffect(() => {
  // The URL ?code= param wins; SIGN_UP_FORM_DEFAULT_VALUES is a local-dev fallback.
  marketingCodeParam.value = (route.query?.code as string) ?? SIGN_UP_FORM_DEFAULT_VALUES?.marketingCode ?? '';
});

// Disable the Continue button while the captcha is loading or until the token is ready.
const isNextButtonDisabled = computed(() => {
  if (step.value === getStepIndexOf(RegistrationStepId.USER_DATA)) {
    // birthDate is only set by the personalize step when the date is valid and
    // the user is 18+, so a non-null value means the field is complete.
    return !name.value?.trim() || !birthDate.value;
  }
  if (step.value === getStepIndexOf(RegistrationStepId.CAPTCHA)) {
    if (captchaIsLoading.value) {
      return true;
    }
    return (captchaRef.value?.isCaptchaActive ?? false) && !captchaRef.value?.captchaToken;
  }
  // On the voucher sub-screen, block "Continue with voucher" until a code is entered.
  if (step.value === getStepIndexOf(RegistrationStepId.TRIAL)) {
    return voucherRevealed.value && !voucherCode.value?.trim();
  }
  return false;
});
</script>
<template>
  <loading-error id="public-sign-up-page" :loading="loading" type="page" :error="error">
    <public-auth-template :footer-items="footerItems" @click-footer-item="onClickFooterItem">
      <sign-up-legal-docs-modal v-if="genericLegalDocs" v-model:legal-model="legalModel" :legal-docs="genericLegalDocs"/>
      <sign-up-verification-sent v-model="showVerification" :email="email" @close="onVerificationClosed"/>
      <!-- The purpose of stepper instead of vue-router page is to have the same page -->
      <!-- that resets to the first one on page refresh -->
      <sign-up-stepper
        v-model="step"
        :show-back-button="step > 0"
        :show-next-button="step !== consentStepIndex && step !== getStepIndexOf(RegistrationStepId.EMAIL)"
        :disable-next-button="isNextButtonDisabled"
        :next-label-key="nextLabelKey"
        :use-custom-cta="step === consentStepIndex"
        :sticky-footer="step === getStepIndexOf(RegistrationStepId.USER_DATA) || step === consentStepIndex"
        @next="goNext"
        @back="goBack"
      >
        <!-- Google sign-ups lose password sign-in the moment the account is linked, and nothing
             else on the way through tells them (IPD-2844). -->
        <template
          v-if="isCompleteSignUp && step === getStepIndexOf(RegistrationStepId.USER_DATA)"
          #cta-note
        >
          <warning-note :text="$t('google_only_notice')"/>
        </template>
        <template #cta-actions>
          <div class="consent-footer">
            <div class="consent-footer__row">
              <balm-btn :label="$t('essentials_only')" theme="grey" :disable="isEssentialsOnlyActive" @click="essentialsOnly"/>
              <balm-btn :label="$t('accept_all')" theme="green" :disable="isAcceptAllActive" @click="onAcceptAll"/>
            </div>
            <div class="consent-footer__row consent-footer__row--nav">
              <balm-btn
                class="consent-footer__back"
                theme="grey"
                icon="arrow-left.svg"
                :title="$t('back')"
                @click="goBack"
              />
              <balm-btn
                class="consent-footer__continue"
                :label="$t('wizard_continue')"
                theme="blue"
                icon="arrow-right.svg"
                :disable="!canContinue"
                @click="goNext"
              />
            </div>
          </div>
        </template>
        <balm-step v-if="steps.includes(RegistrationStepId.EMAIL)" :index="getStepIndexOf(RegistrationStepId.EMAIL)">
          <sign-up-email
            v-model:email="email"
            :google-sign-up-pending="isGoogleSignUpPending"
            :notice="signUpExitMessage"
            :external-error="emailError"
            @finish="step++"
            @clear-external-error="emailError = ''"
          />
        </balm-step>
        <balm-step :index="getStepIndexOf(RegistrationStepId.USER_DATA)">
          <sign-up-personalize
            ref="personalizeRef"
            v-model:name="name"
            v-model:birth-date="birthDate"
            @finish="step++"
          />
        </balm-step>
        <balm-step :index="getStepIndexOf(RegistrationStepId.TRIAL)">
          <sign-up-trial
            ref="trialRef"
            v-model:voucher-code="voucherCode"
            v-model:voucher-revealed="voucherRevealed"
            @voucher-validated="onVoucherValidated"
          />
        </balm-step>
        <balm-step :index="getStepIndexOf(RegistrationStepId.CONSENT)">
          <sign-up-consent-form
            ref="consentFormRef"
            :privacy="currentLegalDocs!.privacyPolicy"
            :terms="currentLegalDocs!.termsAndConditions"
          />
        </balm-step>
        <balm-step v-if="steps.includes(RegistrationStepId.CAPTCHA)" :index="getStepIndexOf(RegistrationStepId.CAPTCHA)">
          <!-- Private Captcha refuses to render unless its widget lives inside a
               <form> ancestor (it logs "cannot find form element" and paints nothing). -->
          <form :class="{'captcha-widget-hidden': captchaScriptFailed}" @submit.prevent>
            <sign-up-captcha
              ref="captchaRef"
              @loading="captchaIsLoading = $event"
              @script-failed="captchaScriptFailed = $event"
            />
          </form>
          <div v-if="captchaScriptFailed" class="captcha-load-error">
            <p>{{ $t('captcha_load_error') }}</p>
            <balm-btn :label="$t('try_again')" theme="blue" @click="captchaRef?.resetAndRemount()"/>
          </div>
        </balm-step>
      </sign-up-stepper>
    </public-auth-template>
  </loading-error>
</template>
<style lang="scss" scoped>
// Two-row consent footer: the primary row holds the two equal "Essentials /
// Accept All" buttons, the nav row holds the fixed-width back arrow and the
// continue button that fills the remaining width.
.consent-footer {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
  width: 100%;
}

.consent-footer__row {
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-4);
  width: 100%;
}

// Every button grows to share its row evenly, except the back arrow.
.consent-footer__row :deep(.balm-btn:not(.consent-footer__back)) {
  flex: 1 1 0;
  width: auto;
  min-width: 0;
  padding-left: var(--Spacing-spacing-4);
  padding-right: var(--Spacing-spacing-4);
}

// Fixed-width pill for the back arrow (overrides the circular icon-only shape).
.consent-footer__row :deep(.consent-footer__back) {
  flex: 0 0 auto;
  width: 64px;
  min-width: 64px;
  border-radius: var(--button-border-radius);
}

// Captcha script-load failure: shown here instead of inside the captcha
// component because carousel slide children don't re-render on their own state.
// The empty widget placeholder is collapsed the same way (page-owned class).
.captcha-widget-hidden :deep(.captcha-container) {
  display: none;
}

.captcha-load-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-4);
  margin-top: var(--Spacing-spacing-4);
  text-align: center;

  p {
    @include font-body;
    margin: 0;
    color: var(--Functional-wl-error-text);
  }
}
</style>
